import React from 'react';
import { deviceQuestions } from './constants';

export const processAnswersForPayload = (answers) => {
  const processedAnswers = {};
  
  Object.entries(answers).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      // Convert array of bilingual objects to comma-separated string
      processedAnswers[key] = value.map(item => {
        if (typeof item === 'object' && item.en && item.fr) {
          return `${item.en}, ${item.fr}`;
        }
        return item;
      }).join(', ');
    } else if (value && value !== '') {
      processedAnswers[key] = value;
    }
  });
  
  return processedAnswers;
};

export const processAnswersFromAPI = (answers) => {
  const processedAnswers = {};
  
  Object.entries(answers).forEach(([key, value]) => {
    if (typeof value === 'string' && value.includes(',')) {
      // Split comma-separated string and convert to bilingual objects
      const values = value.split(',').map(v => v.trim()).filter(v => v);
      const question = deviceQuestions.find(q => q.key === key);
      
      if (question) {
        // Deduplicate by English value to avoid duplicates
        const uniqueValues = new Map();
        values.forEach(val => {
          const option = question.options.find(opt => opt.en === val || opt.fr === val);
          if (option) {
            // Use English value as key to deduplicate
            uniqueValues.set(option.en, option);
          } else {
            uniqueValues.set(val, { en: val, fr: val });
          }
        });
        processedAnswers[key] = Array.from(uniqueValues.values());
      } else {
        // Deduplicate simple values
        const uniqueValues = [...new Set(values)];
        processedAnswers[key] = uniqueValues.map(val => ({ en: val, fr: val }));
      }
    } else {
      processedAnswers[key] = value;
    }
  });
  
  return processedAnswers;
};

export const renderAnswers = (answers, locale, DEVICE_MATRIX) => {
  if (!answers || typeof answers !== 'object') {
    return (DEVICE_MATRIX.MESSAGES && DEVICE_MATRIX.MESSAGES.NO_CONFIGURATION) || 'No configuration';
  }
  
  return (
    <div style={{ 
      maxWidth: '190px',
      width: '160px',
      overflow: 'hidden',
      wordWrap: 'break-word',
      fontSize: '12px',
      lineHeight: '1.3'
    }}>
      {Object.entries(answers).map(([key, value]) => {
        const question = deviceQuestions.find(q => q.key === key);
        let label = key;
        if (question) {
          label = locale === 'en-US' ? question.label.en : question.label.fr;
        }
        
        // Extract values for display based on locale
        let displayValue = value;
        
        if (Array.isArray(value)) {
          displayValue = value.map(item => {
            if (typeof item === 'object' && item.en) {
              return locale === 'en-US' ? item.en : item.fr; // Show based on locale
            }
            return item;
          }).join(', ');
        } else if (typeof value === 'object' && value.en) {
          displayValue = locale === 'en-US' ? value.en : value.en; // Show based on locale
        } else if (typeof value === 'string') {
          // Handle comma-separated strings like "Yes, Oui, No, Non"
          const values = value.split(',').map(v => v.trim());
          const displayValues = [];
          const seenValues = new Set(); // Track unique values to avoid duplicates
          
          if (locale === 'en-US') {
            // Extract English values
            values.forEach(val => {
              if (!seenValues.has(val)) {
                // Filter out known French terms
                const isFrenchTerm = /^(Oui|Non|Nouveau|Ajouter|Voix PLUS|Amélioré|Complet|Complet PLUS|Mobile|Voix|sur une base mensuelle|3 ans|5 ans|Groupé|D’appareils additionnels|Crédit matériel|Version de Connexion Affaires|Type d'installation|Durée|Segment|C2F)$/i.test(val);
                if (!isFrenchTerm) {
                  displayValues.push(val);
                  seenValues.add(val);
                }
              }
            });
          } else {
            // Extract French values
            values.forEach(val => {
              if (!seenValues.has(val)) {
                // Include French terms and neutral terms
                const isEnglishTerm = /^(Yes|No|New|Add On|Voice \+|Enhanced|Complete|Complete \+|Mobile|Voice|Month-to-Month|3 years|5 years|Bundled|Spare Devices|Hardware Credit|Business Connect Edition|Type of Installation|Term|Segment|C2F)$/i.test(val);
                if (!isEnglishTerm || /^(SMB|Commercial|\d+)$/i.test(val)) {
                  displayValues.push(val);
                  seenValues.add(val);
                }
              }
            });
          }
          
          // If we found values, use them; otherwise use original
          displayValue = displayValues.length > 0 ? displayValues.join(', ') : value;
        }
        
        return (
          <div key={key} style={{ 
            marginBottom: '4px', 
            fontSize: '12px',
            lineHeight: '1.5',
            padding: '4px 6px',
            overflow: 'hidden'
          }}>
            <div style={{ fontWeight: '600', color: '#495057', marginBottom: '2px' }}>
              {label}
            </div>
            <div style={{ color: '#6c757d' }}>
              {displayValue}
            </div>
          </div>
        );
      })}
    </div>
  );
};
